import { getYear, isAfter, isValid, subDays } from 'date-fns';
import { validate } from 'email-validator';
import { formatDate, isServerDate } from './formatters';

export function validateEmail(emailAddress: string) {
  if (emailAddress?.trim() && !validate(emailAddress)) {
    return 'Enter a valid email address';
  }
  return true;
}

export function isValidDateWithFutureYear(dateString: string) {
  if (dateString == null) {
    return false;
  }
  const dateValue = new Date(dateString);
  return isValid(dateValue) && getYear(dateValue) >= getYear(new Date());
}

export function isValidDateWithPastYear(dateString: string) {
  if (dateString == null) {
    return false;
  }
  const dateValue = new Date(dateString);
  return isValid(dateValue) && getYear(dateValue) <= getYear(new Date());
}

export function isValidNonNull(value: object | boolean | string | null | undefined) {
  return value === undefined || value === null ? 'An answer is required.' : true;
}

interface DateValidationOptions {
  /**
   * Whether the date must be in the current day or earlier.
   */
  inThePast?: boolean;
  /**
   * Whether the date must be in the future.
   */
  inTheFuture?: boolean;
  /** Max number of years ago for past dates */
  maxYearsAgo?: number;
  /** If the date must be after a certain date */
  mustBeAfter?: {
    value: string;
    message: string;
  };
}

/**
 * Returns true if the date is valid with the "serverDate" format of yyyy-MM-dd, otherwise returns an error message.
 * This is compatible with the react-hook-forms validation function.
 * If the date is null or undefined, it is considered valid since this doesn't check for "required" validations.
 *
 * @param serverDate The date string to validate.
 * @param options Options for date validation. If not provider or empty object, the date is checked for validity only without any range checks
 */
export function validateDate(serverDate: string, options: DateValidationOptions = {}) {
  if (serverDate == null) {
    // We don't validate required
    return true;
  }

  const dateValue = new Date(serverDate);

  if (!isServerDate(serverDate) || !isValid(dateValue)) {
    const pastOrFutureDate = options.inThePast ? 'past date' : options.inTheFuture ? 'future date' : 'date';

    return `Please enter a valid ${pastOrFutureDate}, in the format of ${formatDate(new Date())}`;
  }

  if (options.inThePast && dateValue.valueOf() > new Date().valueOf()) {
    return 'Date must be in the past';
  }

  if (options.inTheFuture && dateValue.valueOf() < subDays(new Date(), 1).valueOf()) {
    return 'Date must be in the future';
  }

  if (options.mustBeAfter && !isAfter(dateValue, options.mustBeAfter.value)) {
    return options.mustBeAfter.message;
  }

  if (options.maxYearsAgo && getYear(new Date()) - getYear(dateValue) > options.maxYearsAgo) {
    return `Date must be within the last ${options.maxYearsAgo} years`;
  }

  return true;
}
